/**
 * Outil « Interpolation de Lagrange » — logique pure (MPSI).
 *
 * Notations de la carte « Polynômes interpolateurs de Lagrange » :
 * Lᵢ = Π_{j≠i} (X − xⱼ) / (xᵢ − xⱼ), P = Σ yᵢ Lᵢ, unique polynôme de
 * degré ≤ n qui passe par les n + 1 points d'abscisses distinctes ;
 * Lᵢ(xⱼ) = 1 si i = j, 0 sinon. Calculs EXACTS (fractions) : abscisses
 * entières, ordonnées au pas de 0,5. L'approximation d'une fonction (Runge)
 * est numérique, et dite approchée.
 */

import { add, frac, fromDecimal, isZero, mul, value, type Frac } from '../../ui/exact';

/** polynôme : coefficients du degré 0 au degré n */
export type Poly = Frac[];

const ZERO = frac(0);
const ONE = frac(1);
const SUB = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
export const idx = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUB[Number(c)])
    .join('');
const SUP: Record<string, string> = {
  2: '²',
  3: '³',
  4: '⁴',
  5: '⁵',
  6: '⁶',
  7: '⁷',
  8: '⁸',
  9: '⁹',
};
const NB = String.fromCharCode(0xa0);

export interface Point {
  x: number;
  y: number;
}

/* ---------- polynômes exacts ---------- */

export function trim(p: Poly): Poly {
  const r = [...p];
  while (r.length > 1 && isZero(r[r.length - 1]!)) r.pop();
  return r.length ? r : [ZERO];
}
/** degré (−∞ pour le polynôme nul) */
export function degree(p: Poly): number {
  const t = trim(p);
  return t.length === 1 && isZero(t[0]!) ? -Infinity : t.length - 1;
}

export function padd(a: Poly, b: Poly): Poly {
  const n = Math.max(a.length, b.length);
  return trim(Array.from({ length: n }, (_, i) => add(a[i] ?? ZERO, b[i] ?? ZERO)));
}
export const pscale = (k: Frac, a: Poly): Poly => trim(a.map((c) => mul(k, c)));
export function pmul(a: Poly, b: Poly): Poly {
  const r: Frac[] = Array.from({ length: a.length + b.length - 1 }, () => ZERO);
  a.forEach((x, i) => b.forEach((y, j) => (r[i + j] = add(r[i + j]!, mul(x, y)))));
  return trim(r);
}
export function peval(p: Poly, x: Frac): Frac {
  let r = ZERO;
  for (let i = p.length - 1; i >= 0; i--) r = add(mul(r, x), p[i]!);
  return r;
}
export const pevalNum = (p: Poly, x: number) => p.reduceRight((r, c) => r * x + value(c), 0);

/** Lᵢ pour les abscisses xs */
export function basis(xs: readonly number[], i: number): Poly {
  let p: Poly = [ONE];
  xs.forEach((xj, j) => {
    if (j === i) return;
    p = pscale(frac(1, xs[i]! - xj), pmul(p, [frac(-xj), ONE]));
  });
  return p;
}

/** P = Σ yᵢ Lᵢ ; les ordonnées sont des décimaux (pas de 0,5) */
export function interpolate(pts: readonly Point[]): Poly {
  const xs = pts.map((p) => p.x);
  let P: Poly = [ZERO];
  pts.forEach((p, i) => {
    P = padd(P, pscale(fromDec(p.y), basis(xs, i)));
  });
  return P;
}

export const fromDec = fromDecimal;

/* ---------- écriture ---------- */

/** « 3 », « (−3) » : un nombre posé dans une différence */
const sgn = (v: number) => (v < 0 ? `(−${-v})` : String(v));

export function fracText(f: Frac): string {
  const s = f.p < 0 ? '−' : '';
  const n = Math.abs(f.p);
  return f.q === 1 ? `${s}${n}` : `${s}${n}/${f.q}`;
}

/** décimal exact si q = 2ⁱ5ʲ (≤ 3 décimales), sinon fraction : « 1,5 », « 2/3 » */
export function numText(f: Frac): string {
  let q = f.q;
  while (q % 2 === 0) q /= 2;
  while (q % 5 === 0) q /= 5;
  const v = value(f);
  if (q === 1 && Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-9)
    return String(Number(v.toFixed(3)))
      .replace('.', ',')
      .replace('-', '−');
  return fracText(f);
}

const mono = (d: number, v: string) => (d === 0 ? '' : d === 1 ? v : `${v}${SUP[d] ?? `^${d}`}`);

/** « −1/2 X² + 3X + 1 » (degrés décroissants), « 0 » pour le polynôme nul */
export function polyText(p: Poly, v = 'X'): string {
  const t = trim(p);
  const parts: string[] = [];
  for (let d = t.length - 1; d >= 0; d--) {
    const c = t[d]!;
    if (isZero(c)) continue;
    const neg = c.p < 0;
    const abs = frac(Math.abs(c.p), c.q);
    // « 2X », « 1/3 X² » : une fraction se sépare de X par une espace insécable
    const coef =
      d === 0
        ? fracText(abs)
        : abs.p === 1 && abs.q === 1
          ? ''
          : abs.q === 1
            ? String(abs.p)
            : `${fracText(abs)}${NB}`;
    const term = `${coef}${mono(d, v)}`;
    parts.push(parts.length === 0 ? `${neg ? '−' : ''}${term}` : `${neg ? '−' : '+'} ${term}`);
  }
  return parts.length ? parts.join(' ') : '0';
}

/** « X(X − 1) / 6 » : forme produit de Lᵢ (X en tête ; dénominateur Π (xᵢ − xⱼ) calculé) */
export function basisProductText(xs: readonly number[], i: number): string {
  const others = xs.filter((_, j) => j !== i);
  const factors = [
    ...others.filter((xj) => xj === 0).map(() => 'X'),
    ...others.filter((xj) => xj !== 0).map((xj) => `(X ${xj > 0 ? '−' : '+'} ${Math.abs(xj)})`),
  ];
  const den = xs.reduce((acc, xj, j) => (j === i ? acc : acc * (xs[i]! - xj)), 1);
  // un facteur seul, sans division : « X + 1 » plutôt que « (X + 1) »
  const num =
    factors.length === 1 && den === 1 ? factors[0]!.replace(/^\((.*)\)$/, '$1') : factors.join('');
  if (den === 1) return num;
  if (den === -1) return `−${num}`;
  return `${den < 0 ? '−' : ''}${num} / ${Math.abs(den)}`;
}

/** « 2 L₀ − L₁ + 0,5 L₂ » : combinaison Σ yᵢ Lᵢ, termes nuls omis, « 0 » si tous nuls */
export function combinationText(ys: readonly number[]): string {
  const terms = ys.flatMap((y, i) => {
    if (y === 0) return [];
    const a = numText(fromDec(Math.abs(y)));
    return [{ neg: y < 0, body: `${a === '1' ? '' : `${a} `}L${idx(i)}` }];
  });
  if (!terms.length) return '0';
  return terms
    .map((t, k) => (k === 0 ? `${t.neg ? '−' : ''}${t.body}` : `${t.neg ? '−' : '+'} ${t.body}`))
    .join(' ');
}

/** « (0 − 1)(0 − 3) » : le dénominateur posé */
export function denominatorText(xs: readonly number[], i: number): string {
  const xi = xs[i]!;
  return xs.flatMap((xj, j) => (j === i ? [] : [`(${sgn(xi)} − ${sgn(xj)})`])).join('');
}

/* ---------- approximation d'une fonction (Runge) ---------- */

export type FnId = 'runge' | 'sin' | 'abs';
export const FUNCTIONS: Record<FnId, { label: string; f: (x: number) => number }> = {
  runge: { label: 'f(x) = 1 / (1 + x²)', f: (x) => 1 / (1 + x * x) },
  sin: { label: 'f(x) = sin x', f: Math.sin },
  abs: { label: 'f(x) = |x| / 2', f: (x) => Math.abs(x) / 2 },
};

/** n + 1 nœuds sur [a ; b] : équirépartis, ou de Tchebychev */
export function nodes(n: number, a: number, b: number, cheb = false): number[] {
  return Array.from({ length: n + 1 }, (_, k) =>
    cheb
      ? (a + b) / 2 + ((b - a) / 2) * Math.cos(((2 * k + 1) * Math.PI) / (2 * (n + 1)))
      : n === 0
        ? (a + b) / 2
        : a + ((b - a) * k) / n,
  );
}

/** valeur en x du polynôme interpolateur (forme de Lagrange, en flottants) */
export function lagrangeAt(xs: readonly number[], ys: readonly number[], x: number): number {
  let s = 0;
  for (let i = 0; i < xs.length; i++) {
    let l = 1;
    for (let j = 0; j < xs.length; j++) if (j !== i) l *= (x - xs[j]!) / (xs[i]! - xs[j]!);
    s += ys[i]! * l;
  }
  return s;
}

/** écart maximal |f − P| sur [a ; b] (échantillonné finement) */
export function maxError(f: (x: number) => number, xs: readonly number[], a: number, b: number) {
  const ys = xs.map(f);
  let m = 0;
  for (let k = 0; k <= 2000; k++) {
    const x = a + ((b - a) * k) / 2000;
    m = Math.max(m, Math.abs(f(x) - lagrangeAt(xs, ys, x)));
  }
  return m;
}

/* ---------- défi ---------- */

export type QuizKind = 'kronecker' | 'line' | 'basisValue' | 'interp3' | 'degree';
export const QUIZ_KINDS: QuizKind[] = ['kronecker', 'line', 'basisValue', 'interp3', 'degree'];

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}
export interface Quiz {
  kind: QuizKind;
  prompt: string;
  options: QuizOption[];
  explain: string;
}

type Rng = () => number;
const int = (lo: number, hi: number, rng: Rng) => lo + Math.floor(rng() * (hi - lo + 1));
function shuffle<T>(a: readonly T[], rng: Rng): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}
/** abscisses entières distinctes, triées */
function distinctXs(n: number, rng: Rng, lo = -3, hi = 3): number[] {
  const s = new Set<number>();
  while (s.size < n) s.add(int(lo, hi, rng));
  return [...s].sort((a, b) => a - b);
}
const ptsText = (pts: readonly Point[]) =>
  pts.map((p) => `(${numText(fromDec(p.x))} ; ${numText(fromDec(p.y))})`).join(', ');

/** garde les 4 premières options de textes distincts (la bonne d'abord) */
function finish(
  kind: QuizKind,
  prompt: string,
  cands: QuizOption[],
  explain: string,
  rng: Rng,
): Quiz | null {
  const seen = new Set<string>();
  const kept: QuizOption[] = [];
  for (const c of cands) {
    if (seen.has(c.text)) continue;
    seen.add(c.text);
    kept.push(c);
  }
  if (kept.length < 4) return null;
  return { kind, prompt, options: shuffle(kept.slice(0, 4), rng), explain };
}

function quizKronecker(rng: Rng): Quiz | null {
  const xs = distinctXs(3, rng);
  const i = int(0, 2, rng);
  const same = rng() < 0.5;
  const j = same ? i : (i + 1 + int(0, 1, rng)) % 3;
  const good = same ? '1' : '0';
  return finish(
    'kronecker',
    `Abscisses x₀ = ${xs[0]}, x₁ = ${xs[1]}, x₂ = ${xs[2]}. Que vaut L${idx(i)}(x${idx(j)}) ?`.replace(
      /-/g,
      '−',
    ),
    [
      { text: good, correct: true },
      {
        text: same ? '0' : '1',
        correct: false,
        hint: same
          ? `L${idx(i)} s’annule en toutes les AUTRES abscisses ; en x${idx(i)}, il vaut 1.`
          : `L${idx(i)} vaut 1 en x${idx(i)} seulement ; en x${idx(j)}, le facteur (X − x${idx(j)}) l’annule.`,
      },
      {
        text: `${xs[j]}`.replace('-', '−'),
        correct: false,
        hint: 'Lᵢ(xⱼ) ne dépend pas de la valeur de xⱼ : c’est 1 ou 0.',
      },
      {
        text: '1/2',
        correct: false,
        hint: 'Lᵢ(xⱼ) vaut exactement 1 (si i = j) ou 0 (si i ≠ j).',
      },
      {
        text: '−1',
        correct: false,
        hint: 'Lᵢ(xⱼ) vaut exactement 1 (si i = j) ou 0 (si i ≠ j).',
      },
    ],
    `L${idx(i)} = Π (X − xⱼ)/(x${idx(i)} − xⱼ) sur j ≠ ${i} : ${same ? `en x${idx(i)}, chaque facteur vaut 1, donc L${idx(i)}(x${idx(i)}) = 1` : `le facteur (X − x${idx(j)}) s’annule en x${idx(j)}, donc L${idx(i)}(x${idx(j)}) = 0`}.`,
    rng,
  );
}

function quizLine(rng: Rng): Quiz | null {
  const [x0, x1] = distinctXs(2, rng);
  const y0 = int(-3, 3, rng);
  const y1 = int(-3, 3, rng);
  if (y0 === y1) return null;
  const pts = [
    { x: x0!, y: y0 },
    { x: x1!, y: y1 },
  ];
  const P = interpolate(pts);
  // erreurs : pente inversée, mauvais point d'appui, x et y échangés
  const slope = frac(y1 - y0, x1! - x0!);
  const inv = padd([frac(y0)], pscale(frac(x1! - x0!, y1 - y0), [frac(-x0!), ONE]));
  const wrongBase = padd([frac(y1)], pscale(slope, [frac(-x0!), ONE]));
  const negSlope = padd([frac(y0)], pscale(frac(-(y1 - y0), x1! - x0!), [frac(-x0!), ONE]));
  return finish(
    'line',
    `Quel est le polynôme interpolateur de Lagrange aux points ${ptsText(pts)} ?`,
    [
      { text: `P = ${polyText(P)}`, correct: true },
      {
        text: `P = ${polyText(inv)}`,
        correct: false,
        hint: 'La pente est (y₁ − y₀)/(x₁ − x₀), pas l’inverse.',
      },
      {
        text: `P = ${polyText(wrongBase)}`,
        correct: false,
        hint: `En X = ${x0}, ce polynôme ne vaut pas ${y0} : vérifie P(x₀) = y₀.`.replace(
          /-/g,
          '−',
        ),
      },
      {
        text: `P = ${polyText(negSlope)}`,
        correct: false,
        hint: 'Signe de la pente : vérifie P(x₁) = y₁.',
      },
    ],
    `Deux points : P est de degré ≤ 1, c’est la droite passant par eux. Pente (${y1} − ${sgn(y0)})/(${x1} − ${sgn(x0!)}) = ${fracText(slope)} ; P = ${polyText(P)}, et P(${x0}) = ${y0}, P(${x1}) = ${y1}.`.replace(
      /-(?=\d)/g,
      '−',
    ),
    rng,
  );
}

/** « L₀(1) = N / D avec N = (1 − (−3))(1 − 0) = 4 et D = (2 − (−3))(2 − 0) = 10, donc L₀(1) = 4/10 = 2/5. » */
function basisValueExplain(xs: number[], i: number, a: number, N: number, D: number, v: Frac) {
  const prod = (t: number) =>
    xs.flatMap((xj, j) => (j === i ? [] : [`(${sgn(t)} − ${sgn(xj)})`])).join('');
  const m = (k: number) => String(k).replace('-', '−');
  const q = `${m(N)}/${D < 0 ? `(${m(D)})` : m(D)}`;
  const end = q === numText(v) ? q : `${q} = ${numText(v)}`;
  return `L${idx(i)}(${m(a)}) = N / D avec N = ${prod(a)} = ${m(N)} et D = ${prod(xs[i]!)} = ${m(D)}, donc L${idx(i)}(${m(a)}) = ${end}.`;
}

function quizBasisValue(rng: Rng): Quiz | null {
  const xs = distinctXs(3, rng);
  const i = int(0, 2, rng);
  const free = [-3, -2, -1, 0, 1, 2, 3, 4].filter((v) => !xs.includes(v));
  const a = free[int(0, free.length - 1, rng)]!;
  const L = basis(xs, i);
  const v = peval(L, frac(a));
  const numOnly = xs.reduce((acc, xj, j) => (j === i ? acc : acc * (a - xj)), 1);
  const den = xs.reduce((acc, xj, j) => (j === i ? acc : acc * (xs[i]! - xj)), 1);
  return finish(
    'basisValue',
    `Abscisses x₀ = ${xs[0]}, x₁ = ${xs[1]}, x₂ = ${xs[2]}. Que vaut L${idx(i)}(${a}) ?`.replace(
      /-/g,
      '−',
    ),
    [
      { text: numText(v), correct: true },
      {
        text: numText(frac(numOnly)),
        correct: false,
        hint: `Il manque le dénominateur ${denominatorText(xs, i)} = ${den}.`.replace(
          /-(?=\d)/g,
          '−',
        ),
      },
      {
        text: numText(frac(-numOnly, den)),
        correct: false,
        hint: 'Signe : (a − xⱼ) au numérateur, (xᵢ − xⱼ) au dénominateur, dans cet ordre.',
      },
      {
        text: numText(frac(den, numOnly || 1)),
        correct: false,
        hint: 'Numérateur et dénominateur sont inversés.',
      },
      {
        text: numText(add(v, ONE)),
        correct: false,
        hint: 'Reprends le produit facteur par facteur.',
      },
    ],
    basisValueExplain(xs, i, a, numOnly, den, v),
    rng,
  );
}

function quizInterp3(rng: Rng): Quiz | null {
  const xs = distinctXs(3, rng, -2, 2);
  const ys = xs.map(() => int(-2, 3, rng));
  if (new Set(ys).size < 2) return null;
  const pts = xs.map((x, i) => ({ x, y: ys[i]! }));
  const P = interpolate(pts);
  if (degree(P) < 2) return null;
  // erreurs : interpoler deux points seulement, ordonnées échangées, Lᵢ sans dénominateur
  const two = interpolate(pts.slice(0, 2));
  const swapped = interpolate([pts[0]!, { ...pts[1]!, y: ys[2]! }, { ...pts[2]!, y: ys[1]! }]);
  let noDen: Poly = [ZERO];
  pts.forEach((p, i) => {
    let num: Poly = [ONE];
    xs.forEach((xj, j) => {
      if (j !== i) num = pmul(num, [frac(-xj), ONE]);
    });
    noDen = padd(noDen, pscale(frac(p.y), num));
  });
  return finish(
    'interp3',
    `Quel est le polynôme interpolateur de Lagrange aux points ${ptsText(pts)} ?`,
    [
      { text: `P = ${polyText(P)}`, correct: true },
      {
        text: `P = ${polyText(two)}`,
        correct: false,
        hint: `Ce polynôme ne passe que par les deux premiers points : il ne vaut pas ${ys[2]} en ${xs[2]}.`.replace(
          /-/g,
          '−',
        ),
      },
      {
        text: `P = ${polyText(swapped)}`,
        correct: false,
        hint: 'Les ordonnées de deux points sont échangées : vérifie P(xᵢ) = yᵢ pour chaque point.',
      },
      {
        text: `P = ${polyText(noDen)}`,
        correct: false,
        hint: 'Chaque Lᵢ se divise par Π (xᵢ − xⱼ) : sans ce dénominateur, Lᵢ(xᵢ) ≠ 1.',
      },
    ],
    `P = ${combinationText(ys)} = ${polyText(P)} ; on vérifie ${pts.map((p) => `P(${p.x}) = ${p.y}`).join(', ')}.`.replace(
      /-(?=\d)/g,
      '−',
    ),
    rng,
  );
}

function quizDegree(rng: Rng): Quiz | null {
  const n = int(2, 6, rng);
  return finish(
    'degree',
    `On interpole ${n + 1} points d’abscisses distinctes. Que peut-on dire du degré du polynôme interpolateur P ?`,
    [
      { text: `deg P ≤ ${n}`, correct: true },
      {
        text: `deg P = ${n}`,
        correct: false,
        hint: `Il peut être plus petit : ${n + 1} points alignés donnent un polynôme de degré ≤ 1.`,
      },
      {
        text: `deg P = ${n + 1}`,
        correct: false,
        hint: `Chaque Lᵢ est un produit de ${n} facteurs (X − xⱼ) : il est de degré ${n}, donc deg P ≤ ${n}.`,
      },
      {
        text: `deg P ≥ ${n}`,
        correct: false,
        hint: 'Le degré peut baisser si des points sont alignés (ou sur une courbe de degré plus petit).',
      },
    ],
    `Chaque Lᵢ est de degré ${n} (produit de ${n} facteurs), donc P = Σ yᵢ Lᵢ est de degré ≤ ${n} ; c’est l’unique polynôme de degré ≤ ${n} qui passe par les ${n + 1} points.`,
    rng,
  );
}

const MAKERS: Record<QuizKind, (rng: Rng) => Quiz | null> = {
  kronecker: quizKronecker,
  line: quizLine,
  basisValue: quizBasisValue,
  interp3: quizInterp3,
  degree: quizDegree,
};

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  for (let i = 0; i < 500; i++) {
    const q = MAKERS[kind](rng);
    if (q) return q;
  }
  throw new Error(`défi ${kind} introuvable`);
}
